import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

// React port of the self-contained TLS/mTLS handshake animation (docs/
// hivemq-tls-mtls-handshake.html), rebuilt on Chakra semantic tokens so it
// tracks the app's light/dark theme toggle. Same step logic as the original.

type Dir = 'ltr' | 'rtl' | 'mid'
type Tone = 'accent' | 'secure' | 'danger'
type Mode = 'valid' | 'invalid'

interface Step {
  phase: 'TLS' | 'mTLS'
  dir: Dir
  sym: '→' | '←' | '✓' | '✗'
  tone: Tone
  label: string
  title: string
  desc: string
  secured?: boolean
  reject?: boolean
  // ids of scene nodes/components to highlight (accent) or mark rejected
  hl?: string[]
  rejectHl?: string[]
}

const CLIENT_W = 158
const BROKER_W = 236
const WIRE_TOP = 64

function makeSteps(mode: Mode): Step[] {
  const base: Step[] = [
    {
      phase: 'TLS',
      dir: 'ltr',
      sym: '→',
      tone: 'accent',
      label: 'ClientHello',
      hl: ['nClient'],
      title: '1 · Client opens a connection',
      desc: 'The MQTT client connects and offers the TLS versions and cipher suites it supports. No certificates are exchanged yet - it is just proposing how to talk securely.',
    },
    {
      phase: 'TLS',
      dir: 'rtl',
      sym: '←',
      tone: 'accent',
      label: 'Server certificate',
      hl: ['nBroker', 'cBrokerKs'],
      title: '2 · Broker presents its server certificate',
      desc: 'HiveMQ takes its server certificate and private key from the keystore and sends the certificate, together with its CA chain, back to the client.',
    },
    {
      phase: 'TLS',
      dir: 'mid',
      sym: '✓',
      tone: 'secure',
      label: 'Validate hostname + chain',
      hl: ['nClient'],
      title: '3 · Client checks the broker identity',
      desc: 'The client verifies the certificate chain and confirms the broker hostname it dialed matches a SAN entry on the certificate. Matching SAN + trusted chain means the broker is who it claims to be.',
    },
    {
      phase: 'TLS',
      dir: 'mid',
      sym: '✓',
      tone: 'secure',
      secured: true,
      label: 'Encrypted channel',
      hl: ['nClient', 'nBroker'],
      title: '4 · Encrypted channel established (one-way TLS)',
      desc: 'Session keys are agreed and all traffic is now encrypted. The client trusts the broker - but so far the broker has no idea who the client is.',
    },
    {
      phase: 'mTLS',
      dir: 'rtl',
      sym: '←',
      tone: 'accent',
      secured: true,
      label: 'Certificate request',
      hl: ['nBroker'],
      title: '5 · Broker asks the client for a certificate',
      desc: 'With mutual TLS enabled, the broker takes an extra step and requests a certificate from the client as part of the same handshake.',
    },
  ]

  if (mode === 'valid') {
    base.push(
      {
        phase: 'mTLS',
        dir: 'ltr',
        sym: '→',
        tone: 'accent',
        secured: true,
        label: 'Client certificate',
        hl: ['nClient', 'cClientKs'],
        title: '6 · Client presents its certificate',
        desc: 'The client sends its own certificate from its keystore and proves it holds the matching private key.',
      },
      {
        phase: 'mTLS',
        dir: 'mid',
        sym: '✓',
        tone: 'secure',
        secured: true,
        label: 'Validate issuer via truststore',
        hl: ['nBroker', 'cBrokerTs'],
        title: '7 · Broker validates the client issuer',
        desc: 'HiveMQ checks the client certificate against its truststore, which holds the root and intermediate CA. It confirms the certificate was issued by a trusted CA - validating the issuer of the chain, not any one specific client identity.',
      },
      {
        phase: 'mTLS',
        dir: 'mid',
        sym: '✓',
        tone: 'secure',
        secured: true,
        label: 'mTLS secured',
        hl: ['nClient', 'nBroker'],
        title: '8 · Mutual trust established (mTLS)',
        desc: 'Both sides have now proven their identity. The MQTT session runs over a fully mutually-authenticated, encrypted channel.',
      },
    )
  } else {
    base.push(
      {
        phase: 'mTLS',
        dir: 'ltr',
        sym: '→',
        tone: 'accent',
        secured: true,
        label: 'Client certificate',
        hl: ['nClient', 'cClientKs'],
        title: '6 · Client presents an untrusted certificate',
        desc: 'The client sends a certificate from its keystore - but this one was issued by a CA that is not in the broker’s truststore.',
      },
      {
        phase: 'mTLS',
        dir: 'mid',
        sym: '✗',
        tone: 'danger',
        secured: true,
        label: 'Issuer not found',
        hl: ['nBroker'],
        rejectHl: ['cBrokerTs'],
        title: '7 · Broker cannot validate the issuer',
        desc: 'HiveMQ looks up the certificate’s issuer in its truststore and finds no matching root or intermediate CA. The chain is untrusted, so the certificate is rejected.',
      },
      {
        phase: 'mTLS',
        dir: 'mid',
        sym: '✗',
        tone: 'danger',
        reject: true,
        label: 'Handshake rejected',
        hl: [],
        rejectHl: ['nBroker'],
        title: '8 · Connection refused',
        desc: 'The broker aborts the TLS handshake and closes the connection. The client is never allowed onto the MQTT session.',
      },
    )
  }
  return base
}

const toneToPalette: Record<Tone, string> = { accent: 'blue', secure: 'green', danger: 'red' }
const symIcon = { '→': ArrowRight, '←': ArrowLeft, '✓': Check, '✗': X } as const

/** A node/component box in the scene, highlighted accent / rejected / neutral. */
function SceneBox({
  active,
  reject,
  children,
  ...rest
}: {
  active?: boolean
  reject?: boolean
  children: React.ReactNode
  [key: string]: unknown
}) {
  const palette = reject ? 'red' : 'blue'
  return (
    <Box
      colorPalette={palette}
      borderWidth="1px"
      borderRadius="md"
      transition="border-color 0.3s, background 0.3s"
      borderColor={active || reject ? 'colorPalette.solid' : 'border'}
      bg={active || reject ? 'colorPalette.subtle' : 'transparent'}
      {...rest}
    >
      {children}
    </Box>
  )
}

export function TlsHandshakeSimulation() {
  const [mode, setMode] = useState<Mode>('valid')
  const [step, setStep] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [sceneWidth, setSceneWidth] = useState(600)
  const [packetLeft, setPacketLeft] = useState(0)
  const [packetTransition, setPacketTransition] = useState('none')
  const sceneRef = useRef<HTMLDivElement>(null)

  const steps = useMemo(() => makeSteps(mode), [mode])
  const st = steps[step]

  // Track scene width so the packet travel endpoints stay correct on resize.
  useLayoutEffect(() => {
    const el = sceneRef.current
    if (!el) {
      return
    }
    const measure = () => setSceneWidth(el.offsetWidth || 600)
    measure()
    if (typeof ResizeObserver === 'undefined') {
      return
    }
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const start = sceneWidth ? CLIENT_W + 10 : 168
  const end = Math.max(start + 40, sceneWidth - BROKER_W - 10)
  const mid = (start + end) / 2

  // Drive the packet position/animation whenever the step (or geometry) changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: start/end/mid derive from step+sceneWidth
  useEffect(() => {
    if (st.dir === 'mid') {
      setPacketTransition('left 0.4s ease')
      setPacketLeft(mid)
      return
    }
    const from = st.dir === 'ltr' ? start : end
    const to = st.dir === 'ltr' ? end : start
    setPacketTransition('none')
    setPacketLeft(from)
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        setPacketTransition('left 1.5s ease')
        setPacketLeft(to)
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      cancelAnimationFrame(raf2)
    }
  }, [step, sceneWidth, mode])

  // Auto-advance while playing; stop at the last step.
  useEffect(() => {
    if (!playing) {
      return
    }
    const t = setInterval(() => {
      setStep((prev) => {
        if (prev >= steps.length - 1) {
          setPlaying(false)
          return prev
        }
        return prev + 1
      })
    }, 2600)
    return () => clearInterval(t)
  }, [playing, steps.length])

  const go = (n: number) => {
    setPlaying(false)
    setStep((n + steps.length) % steps.length)
  }
  const switchMode = (m: Mode) => {
    setPlaying(false)
    setMode(m)
    setStep(0)
  }
  const togglePlay = () => {
    if (playing) {
      setPlaying(false)
    } else {
      if (step >= steps.length - 1) {
        setStep(0)
      }
      setPlaying(true)
    }
  }

  const hl = new Set(st.hl ?? [])
  const rejectHl = new Set(st.rejectHl ?? [])
  const PacketIcon = symIcon[st.sym]
  const packetPalette = toneToPalette[st.tone]
  const phasePalette = st.reject ? 'red' : st.phase === 'mTLS' ? 'green' : 'blue'
  const wireColor = st.reject ? 'red.solid' : st.secured ? 'green.solid' : 'border'

  return (
    <VStack align="stretch" gap={4}>
      {/* Scenario toggle */}
      <HStack gap={2.5} flexWrap="wrap">
        <Text fontSize="xs" color="content.tertiary">
          Scenario
        </Text>
        <HStack gap={0} borderWidth="1px" borderColor="border" borderRadius="md" overflow="hidden">
          <Button
            size="xs"
            variant="ghost"
            borderRadius={0}
            colorPalette="blue"
            bg={mode === 'valid' ? 'colorPalette.subtle' : 'transparent'}
            color={mode === 'valid' ? 'colorPalette.fg' : 'content.secondary'}
            onClick={() => switchMode('valid')}
          >
            Trusted client
          </Button>
          <Button
            size="xs"
            variant="ghost"
            borderRadius={0}
            colorPalette="red"
            borderLeftWidth="1px"
            borderColor="border"
            bg={mode === 'invalid' ? 'colorPalette.subtle' : 'transparent'}
            color={mode === 'invalid' ? 'colorPalette.fg' : 'content.secondary'}
            onClick={() => switchMode('invalid')}
          >
            Untrusted client
          </Button>
        </HStack>
      </HStack>

      {/* Scene */}
      <Box ref={sceneRef} position="relative" w="100%" h="172px">
        {/* Client node */}
        <SceneBox
          active={hl.has('nClient')}
          position="absolute"
          top={0}
          left={0}
          w={`${CLIENT_W}px`}
          p="10px 12px"
        >
          <Text fontSize="13px" fontWeight="medium" color="content.primary">
            MQTT client
          </Text>
          <Text fontSize="11px" color="content.tertiary" mb={2}>
            publisher / subscriber
          </Text>
          <SceneBox active={hl.has('cClientKs')} p="6px 8px">
            <Text fontSize="11px" fontWeight="medium" color="content.secondary">
              Client keystore
            </Text>
            <Text fontSize="11px" color="content.tertiary">
              {mode === 'valid' ? 'client cert + private key' : 'cert from an untrusted CA'}
            </Text>
          </SceneBox>
        </SceneBox>

        {/* Broker node */}
        <SceneBox
          active={hl.has('nBroker')}
          reject={rejectHl.has('nBroker')}
          position="absolute"
          top={0}
          right={0}
          w={`${BROKER_W}px`}
          p="10px 12px"
        >
          <Text fontSize="13px" fontWeight="medium" color="content.primary">
            HiveMQ broker
          </Text>
          <Text fontSize="11px" color="content.tertiary" mb={2}>
            TLS listener
          </Text>
          <SceneBox active={hl.has('cBrokerKs')} p="6px 8px" mb="6px">
            <Text fontSize="11px" fontWeight="medium" color="content.secondary">
              Keystore
            </Text>
            <Text fontSize="11px" color="content.tertiary">
              server cert (hostname in SAN) + private key
            </Text>
          </SceneBox>
          <SceneBox active={hl.has('cBrokerTs')} reject={rejectHl.has('cBrokerTs')} p="6px 8px">
            <Text fontSize="11px" fontWeight="medium" color="content.secondary">
              Truststore
            </Text>
            <Text fontSize="11px" color="content.tertiary">
              root + intermediate CA
            </Text>
          </SceneBox>
        </SceneBox>

        {/* Wire */}
        <Box
          position="absolute"
          top={`${WIRE_TOP}px`}
          left={`${start}px`}
          w={`${end - start}px`}
          borderTopWidth="2px"
          borderStyle={st.secured || st.reject ? 'solid' : 'dashed'}
          borderColor={wireColor}
          transition="border-color 0.3s"
        />

        {/* Packet */}
        <Flex
          position="absolute"
          top={`${WIRE_TOP}px`}
          align="center"
          gap={1.5}
          transform="translate(-50%, -50%)"
          colorPalette={packetPalette}
          bg="bg.panel"
          borderWidth="1px"
          borderColor="colorPalette.solid"
          color="colorPalette.fg"
          fontSize="11px"
          fontWeight="medium"
          px={2.5}
          py={1}
          borderRadius="full"
          whiteSpace="nowrap"
          style={{ left: `${packetLeft}px`, transition: packetTransition }}
        >
          <PacketIcon size={13} />
          <span>{st.label}</span>
        </Flex>
      </Box>

      {/* Caption */}
      <Box borderTopWidth="1px" borderColor="border" pt={3}>
        <Text fontSize="15px" fontWeight="medium" color="content.primary" mb={1}>
          {st.title}
        </Text>
        <Text fontSize="13px" color="content.secondary" lineHeight="1.55" minH="60px">
          {st.desc}
        </Text>
      </Box>

      {/* Controls */}
      <HStack gap={2} flexWrap="wrap">
        <Box
          colorPalette={phasePalette}
          bg="colorPalette.subtle"
          color="colorPalette.fg"
          fontSize="11px"
          fontWeight="medium"
          px={2.5}
          py={1}
          borderRadius="full"
        >
          {st.reject ? 'rejected' : st.phase}
        </Box>
        <Button size="xs" variant="outline" onClick={() => go(step - 1)}>
          ‹ Back
        </Button>
        <Button size="xs" variant="outline" minW="88px" onClick={togglePlay}>
          {playing ? '❙❙ Pause' : '▶ Play'}
        </Button>
        <Button size="xs" variant="outline" onClick={() => go(step + 1)}>
          Next ›
        </Button>
        <HStack gap={1.5} ml="auto" align="center">
          {steps.map((s, k) => (
            <Box key={`${s.phase}-${s.title}`} display="flex" alignItems="center" gap={1.5}>
              {k === 4 ? <Box w="1px" h="12px" bg="border" /> : null}
              <Box
                boxSize="7px"
                borderRadius="full"
                colorPalette={mode === 'invalid' && k >= 4 ? 'red' : 'blue'}
                bg={k === step ? 'colorPalette.solid' : 'border'}
              />
            </Box>
          ))}
        </HStack>
      </HStack>
    </VStack>
  )
}
